import {FilterParameters} from '@findhotel/sapi'
import {SeoAttributes} from 'seo/seo-db/attributes'

import {logger} from '@vio/core/src/utils/logging/logger'

const log = logger.child({module: 'getSlugFromFilters'})

type FilterKey = keyof FilterParameters
type SlugMap = Partial<Record<FilterKey, Record<string, string>>>

const slugMapCache = new WeakMap<SeoAttributes, SlugMap>()

const buildSlugMap = (mapping: SeoAttributes): SlugMap => {
  const result: SlugMap = {}

  for (const [slug, config] of Object.entries(mapping)) {
    if (!config.value || !config.key) {
      log.warn('Invalid filter config', config)
      continue
    }

    if (!result[config.key]) result[config.key] = {}

    result[config.key][config.value] = slug
    if (config.aliasValue) result[config.key][config.aliasValue] = slug
  }

  return result
}

const getSlugMap = (attributes: SeoAttributes): SlugMap => {
  const cached = slugMapCache.get(attributes)
  if (cached) return cached

  const slugMap = buildSlugMap(attributes)
  slugMapCache.set(attributes, slugMap)
  return slugMap
}

/**
 * Returns the SEO slug for a given filter within a locale's attributes, or null.
 */
const getSlugFromFilters = (
  key: FilterKey,
  value: string,
  attributes: SeoAttributes
): string | null => {
  const slugMap = getSlugMap(attributes)
  return slugMap[key]?.[value] ?? null
}

/**
 * Returns the SEO slug for a facility id within the given locale's attributes.
 * Pass the current locale's attributes from the `getSeoActiveAttributes`
 * selector. Returns null when there are no attributes.
 */
export const getFacilitySeoSlug = (
  facilityId: number,
  attributes: SeoAttributes | undefined
): string | null =>
  attributes
    ? getSlugFromFilters('facilities', String(facilityId), attributes)
    : null
